// OrganismDisplayViewEduPreviewMobile.jsx
// 모바일 전용 교육 콘텐츠 미리보기 컴포넌트 (전체화면 기능 없음)

import React, { useEffect, useState, useRef, useCallback } from "react";
import styled from "styled-components";
import { toPng, toSvg } from "html-to-image";
import { palette } from "../../../../assets/styles/Palette";
import { Body3 } from "../../../../assets/styles/Typography";
import { Button } from "../../../../assets/styles/ButtonStyle";
import { useAtom } from "jotai";
import MoleculeSizeArea from "../molecules/MoleculeSizeArea";
import MoleculeBackgroundModern from "../../../CreateEducationContentPage/components/molecules/MoleculeBackgroundModern";
import MoleculeBackgroundSimple from "../../../CreateEducationContentPage/components/molecules/MoleculeBackgroundSimple";
import MoleculeBackgroundSimpleWide from "../../../CreateEducationContentPage/components/molecules/MoleculeBackgroundSimpleWide";
import Markdown from "markdown-to-jsx";

const OrganismDisplayViewEduPreviewMobile = ({
  template,
  onClose,
  title,
  jsonData,
  markdownData,
  titleData,
  contentsPurpose,
  contentsDesignConcept,
  displayWidth,
  displayHeight,
}) => {
  console.log(
    "📱 모바일 전용 미리보기 (전체화면 기능 없음):",
    contentsDesignConcept
  );

  const previewRef = useRef(null);
  const downloadRef = useRef(null); // PNG 생성용 별도 ref

  const [originalSize, setOriginalSize] = useState({
    width: 3840,
    height: 2160,
  });

  const [previewSize, setPreviewSize] = useState({
    width: 800,
    height: 450,
  });

  // displayWidth와 displayHeight가 변경될 때 원본 사이즈 업데이트
  useEffect(() => {
    console.log("DisplayWidth:", displayWidth, "DisplayHeight:", displayHeight);
    if (displayWidth && displayHeight) {
      const width = parseInt(displayWidth);
      const height = parseInt(displayHeight);

      setOriginalSize({
        width: width,
        height: height,
      });

      // 화면에 맞는 미리보기 사이즈 계산 (화면을 완전히 채우도록)
      const maxWidth = window.innerWidth;
      const maxHeight = window.innerHeight - 120; // 헤더와 사이즈 영역 높이 고려

      // 화면을 완전히 채우도록 설정 (비율 무시)
      setPreviewSize({
        width: maxWidth,
        height: maxHeight,
      });
    }
  }, [displayWidth, displayHeight]);

  // 창 크기 변경시 미리보기 사이즈 재계산
  useEffect(() => {
    const handleResize = () => {
      const maxWidth = window.innerWidth;
      const maxHeight = window.innerHeight - 120; // 헤더와 사이즈 영역 높이 고려

      // 화면을 완전히 채우도록 설정 (비율 무시)
      setPreviewSize({
        width: maxWidth,
        height: maxHeight,
      });
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [originalSize]);

  const handleDownloadPng = useCallback(() => {
    if (downloadRef.current === null) {
      return;
    }

    // downloadRef 요소를 임시로 보이게 만들기
    const downloadElement = downloadRef.current;
    downloadElement.style.position = "fixed";
    downloadElement.style.top = "0";
    downloadElement.style.left = "0";
    downloadElement.style.zIndex = "10000";

    // 약간의 지연을 두어 렌더링 완료 후 캡처
    setTimeout(() => {
      toPng(downloadElement, {
        cacheBust: true,
        width: originalSize.width,
        height: originalSize.height,
        style: {
          transform: "none",
          width: `${originalSize.width}px`,
          height: `${originalSize.height}px`,
        },
      })
        .then((dataUrl) => {
          const link = document.createElement("a");
          link.download = `${title || "template"}.png`;
          link.href = dataUrl;
          link.click();

          // 다시 숨기기
          downloadElement.style.position = "absolute";
          downloadElement.style.top = "-9999px";
          downloadElement.style.left = "-9999px";
          downloadElement.style.zIndex = "-1";
        })
        .catch((err) => {
          console.log(err);

          // 에러 시에도 다시 숨기기
          downloadElement.style.position = "absolute";
          downloadElement.style.top = "-9999px";
          downloadElement.style.left = "-9999px";
          downloadElement.style.zIndex = "-1";
        });
    }, 100);
  }, [originalSize, title]);

  const handleDownloadSvg = useCallback(() => {
    if (downloadRef.current === null) {
      return;
    }

    // downloadRef 요소를 임시로 보이게 만들기
    const downloadElement = downloadRef.current;
    downloadElement.style.position = "fixed";
    downloadElement.style.top = "0";
    downloadElement.style.left = "0";
    downloadElement.style.zIndex = "10000";

    // 약간의 지연을 두어 렌더링 완료 후 캡처
    setTimeout(() => {
      toSvg(downloadElement, {
        cacheBust: true,
        width: originalSize.width,
        height: originalSize.height,
        style: {
          transform: "none",
          width: `${originalSize.width}px`,
          height: `${originalSize.height}px`,
        },
      })
        .then((dataUrl) => {
          const link = document.createElement("a");
          link.download = `${title || "template"}.svg`;
          link.href = dataUrl;
          link.click();

          // 다시 숨기기
          downloadElement.style.position = "absolute";
          downloadElement.style.top = "-9999px";
          downloadElement.style.left = "-9999px";
          downloadElement.style.zIndex = "-1";
        })
        .catch((err) => {
          console.log(err);

          // 에러 시에도 다시 숨기기
          downloadElement.style.position = "absolute";
          downloadElement.style.top = "-9999px";
          downloadElement.style.left = "-9999px";
          downloadElement.style.zIndex = "-1";
        });
    }, 100);
  }, [originalSize, title]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const handlePopState = () => {
      onClose();
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("popstate", handlePopState);
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("popstate", handlePopState);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose]);

  const handleGoBack = () => {
    onClose();
  };

  // 미리보기용 컨텐츠 (화면을 완전히 채우도록)
  const renderPreviewContent = () => {
    const finalTitleData = titleData || [];
    const finalMarkdownData = markdownData || [];

    return (
      <div
        ref={previewRef}
        style={{
          width: previewSize.width,
          height: previewSize.height,
          transformOrigin: "top left",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            width: originalSize.width,
            height: originalSize.height,
            transform: `scale(${Math.min(
              previewSize.width / originalSize.width,
              previewSize.height / originalSize.height
            )})`,
            transformOrigin: "top left",
          }}
        >
          <MoleculeBackgroundSimpleWide
            contentsDesignConcept={contentsDesignConcept}
            title={finalTitleData}
            content={finalMarkdownData}
          />
        </div>
      </div>
    );
  };

  return (
    <PreviewContainer>
      <Header>
        <TitleContainer>
          <TemplateCategory>
            <Body3 color="primary" style={{ fontSize: "12px" }}>
              {template?.category || "디자인 컨셉"}
            </Body3>
          </TemplateCategory>
          <HeaderTitle>{title || "미리보기"}</HeaderTitle>
        </TitleContainer>
        <ButtonGroup>
          <Button Large onClick={handleDownloadPng}>
            PNG로 저장
          </Button>
          <Button Large onClick={handleDownloadSvg}>
            SVG로 저장
          </Button>
          <Button Primary Large Fill onClick={handleGoBack}>
            미리보기 종료하기
          </Button>
        </ButtonGroup>
      </Header>

      <StyledSizeAreaContainer>
        <MoleculeSizeArea
          title="내 디스플레이"
          width={
            displayWidth
              ? displayWidth.toString()
              : originalSize.width.toString()
          }
          height={
            displayHeight
              ? displayHeight.toString()
              : originalSize.height.toString()
          }
        />
      </StyledSizeAreaContainer>

      <PreviewContent
        onContextMenu={(e) => {
          e.preventDefault();
          // 컨텍스트 메뉴 대신 간단한 알림이나 동작
          const action = window.confirm(
            "미리보기를 종료하시겠습니까?\n확인: 종료\n취소: PNG 저장"
          );
          if (action) {
            handleGoBack();
          } else {
            handleDownloadPng();
          }
        }}
      >
        {renderPreviewContent()}
      </PreviewContent>

      {/* PNG/SVG 생성용 숨겨진 원본 사이즈 요소 */}
      <div
        ref={downloadRef}
        style={{
          position: "absolute",
          top: "-9999px",
          left: "-9999px",
          width: originalSize.width,
          height: originalSize.height,
          background: "white",
          zIndex: -1,
        }}
      >
        <MoleculeBackgroundSimpleWide
          contentsDesignConcept={contentsDesignConcept}
          title={titleData}
          content={markdownData}
        />
      </div>
    </PreviewContainer>
  );
};

export default OrganismDisplayViewEduPreviewMobile;

const StyledMainDescription = styled.span`
  color: black;
  font-size: 32px;
  font-family: Pretendard;
  font-weight: 500;
  line-height: 48px;
  word-wrap: break-word;
  text-align: start;
`;

const StyledThickLine = styled.div`
  align-self: stretch;
  height: 0px;
  outline: 3px black solid;
  outline-offset: -2px;
`;

const StyledSectionTitle = styled.span`
  width: 144px;
  color: black;
  font-size: 32px;
  font-family: Pretendard;
  font-weight: 500;
  line-height: 48px;
  word-wrap: break-word;
  text-align: start;
`;

const StyledSectionContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledBulletItem = styled.div`
  width: 688px;
  color: black;
  font-size: 32px;
  font-family: Pretendard;
  font-weight: 400;
  line-height: 48px;
  word-wrap: break-word;
  text-align: start;
  padding-left: 32px;
  text-indent: -22px;
`;

const StyledThinLine = styled.div`
  align-self: stretch;
  height: 0px;
  outline: 1px black solid;
  outline-offset: 0px;
`;

const StyledSectionRow = styled.div`
  justify-content: flex-start;
  align-items: flex-start;
  gap: 32px;
  display: inline-flex;
`;

const StyledContentContainerForModern = styled.div`
  width: 100%;
  height: 100%;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 16px;
  display: inline-flex;
`;

const PreviewContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  background: #ebebeb;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
`;

const Header = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 7px 14px;
  background: white;
  border-bottom: 1px solid #e0e0e0;
  flex-shrink: 0;
`;

const TitleContainer = styled.div`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 12px;
`;

const HeaderTitle = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: ${palette.gray800};
`;

const PreviewContent = styled.div`
  flex: 1;
  overflow: hidden;
  padding: 0;
  margin: 0;
  background: transparent;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;

  &::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
  }
  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
`;

const TemplateCategory = styled.div`
  color: ${palette.primary};
  font-weight: 500;
  background-color: ${palette.primaryLightest};
  padding: 3px 8px;
  border-radius: 2px;
`;

const StyledSizeAreaContainer = styled.div`
  top: 56px;
  left: 0;
  right: 0;
  z-index: 99;
`;
